import { useState } from 'react'
import { ReplyCard } from './ReplyCard'
import { useRepliesByReview } from '../queries/useReviews'
import { ReportReplyDialog } from './dialogs/ReportReplyDialog'
import { OkDialog } from './dialogs/OkDialog'
import { pb } from '../pocketbase/Pocketbase'
import { useAuthStore } from '../data/useAuthStore'

interface RepliesSectionProps {
  reviewId: string
  highlightReplyId?: string | null
}

export const RepliesSection = ({ reviewId, highlightReplyId }: RepliesSectionProps) => {
  const { userProfile } = useAuthStore()
  const { data: replies = [], isLoading } = useRepliesByReview(reviewId)
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false)
  const [isOkDialogOpen, setIsOkDialogOpen] = useState(false)
  const [reportingReplyId, setReportingReplyId] = useState<string | null>(null)

  const handleReport = (replyId: string) => {
    if (!pb.authStore.isValid) {
      setIsOkDialogOpen(true)
      return
    }
    setReportingReplyId(replyId)
    setIsReportDialogOpen(true)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    if (diffDays < 14) return '1 week ago'
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
    if (diffDays < 60) return '1 month ago'
    return `${Math.floor(diffDays / 30)} months ago`
  }

  if (isLoading) {
    return null
  }

  if (replies.length === 0) {
    return null
  }

  return (
    <>
      <div className={'ml-8 mb-6'}>
        {replies.map((reply) => (
          <ReplyCard
            key={reply.id}
            username={reply.username}
            replyText={reply.text}
            date={formatDate(reply.created)}
            isOwnReply={reply.userId === userProfile?.id}
            isHighlighted={reply.id === highlightReplyId}
            onReport={() => handleReport(reply.id)}
          />
        ))}
      </div>

      <ReportReplyDialog
        isOpen={isReportDialogOpen}
        onClose={() => {
          setIsReportDialogOpen(false)
          setReportingReplyId(null)
        }}
        replyId={reportingReplyId}
      />

      <OkDialog
        isOpen={isOkDialogOpen}
        onClose={() => setIsOkDialogOpen(false)}
        title="Login Required"
        content="Please log in to report a reply"
        okLabel="OK"
      />
    </>
  )
}
